import { readFromSupabase, writeToSupabase } from './supabase'

export interface VersionSnapshot {
  id: string
  timestamp: string
  label: string
  data: any
}

export interface VersionHistory {
  snapshots: VersionSnapshot[]
}

const MAX_SNAPSHOTS = 25

function generateId(): string {
  return Math.random().toString(36).substr(2, 9) + Date.now().toString(36)
}

function getHistoryKey(contentKey: string): string {
  return contentKey + '_history'
}

export async function saveSnapshot(
  contentKey: string,
  currentData: any,
  label: string = 'Auto-save',
): Promise<void> {
  const historyKey = getHistoryKey(contentKey)
  const existing = await readFromSupabase<VersionHistory>(historyKey)
  const history: VersionHistory = existing || { snapshots: [] }

  const snapshot: VersionSnapshot = {
    id: generateId(),
    timestamp: new Date().toISOString(),
    label,
    data: JSON.parse(JSON.stringify(currentData)),
  }

  history.snapshots.unshift(snapshot)

  // Keep only the last MAX_SNAPSHOTS
  if (history.snapshots.length > MAX_SNAPSHOTS) {
    history.snapshots = history.snapshots.slice(0, MAX_SNAPSHOTS)
  }

  await writeToSupabase(historyKey, history)
}

export async function getSnapshots(
  contentKey: string,
): Promise<VersionSnapshot[]> {
  const historyKey = getHistoryKey(contentKey)
  const history = await readFromSupabase<VersionHistory>(historyKey)
  return history?.snapshots || []
}

export async function deleteSnapshot(
  contentKey: string,
  snapshotId: string,
): Promise<void> {
  const historyKey = getHistoryKey(contentKey)
  const history = await readFromSupabase<VersionHistory>(historyKey)
  if (!history) return

  history.snapshots = history.snapshots.filter((s) => s.id !== snapshotId)
  await writeToSupabase(historyKey, history)
}

export async function clearHistory(contentKey: string): Promise<void> {
  const historyKey = getHistoryKey(contentKey)
  await writeToSupabase(historyKey, { snapshots: [] })
}

// Format timestamp for display
export function formatTimestamp(iso: string): string {
  const date = new Date(iso)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return diffMins + 'm ago'
  if (diffHours < 24) return diffHours + 'h ago'
  if (diffDays < 7) return diffDays + 'd ago'

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  })
}
